import { useState } from "react";
import { useAuth } from "contexts/AuthContext";
import { RefreshCcw, CloudDownload, Link, CheckCircle, AlertCircle, RefreshCw, Loader2, Copy, ToggleLeft, ToggleRight } from "lucide-react";

export default function SyncPanel() {
  const { api } = useAuth();
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [msg, setMsg] = useState(null);
  const [syncEnabled, setSyncEnabled] = useState(true);
  const [copied, setCopied] = useState(false);

  const fetchStatus = async () => {
    try {
      const res = await api.get("/sync/status");
      setSyncStatus(res.data);
      setLoaded(true);
    } catch { setSyncStatus({ connected: false, error: "Failed to check" }); setLoaded(true); }
  };

  if (!loaded) fetchStatus();

  const handleSync = async (endpoint) => {
    setSyncing(true); setMsg(null);
    try {
      const res = await api.post(`/sync/${endpoint}`);
      setMsg({ type: "success", text: res.data.message || "Sync completed" });
    } catch (err) {
      setMsg({ type: "error", text: err.response?.data?.detail || "Sync failed" });
    } finally {
      setSyncing(false);
    }
  };

  const copyEmail = () => {
    if (syncStatus?.service_account_email) {
      navigator.clipboard.writeText(syncStatus.service_account_email);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const isSyncAvailable = syncEnabled && syncStatus?.connected;

  return (
    <div className="sync-section" data-testid="sync-section">
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:16}}>
        <h2 className="section-title" style={{margin:0}}><RefreshCcw size={20} color="#ff4500" /> Google Sheets Sync</h2>
        <button
          className={`sync-toggle-btn ${syncEnabled ? 'enabled' : 'disabled'}`}
          onClick={() => setSyncEnabled(prev => !prev)}
          data-testid="sync-toggle-btn"
          title={syncEnabled ? 'Click to disable sync' : 'Click to enable sync'}
        >
          {syncEnabled ? <ToggleRight size={22} /> : <ToggleLeft size={22} />}
          <span>{syncEnabled ? 'Sync Enabled' : 'Sync Disabled'}</span>
        </button>
      </div>

      {!syncEnabled && (
        <div className="sync-disabled-banner" data-testid="sync-disabled-banner">
          <AlertCircle size={16} />
          <span>Sync is currently disabled. Enable it above to use Google Sheets sync features.</span>
        </div>
      )}

      {msg && (
        <div className={`toast ${msg.type}`} style={{position:'static',margin:'12px 0'}}>
          {msg.type === 'success' ? <CheckCircle size={14}/> : <AlertCircle size={14}/>} {msg.text}
        </div>
      )}

      <div className="sync-status-card" data-testid="sync-status">
        <div className="sync-status-header">
          <h3>Connection Status</h3>
          <button className="btn btn-sm btn-secondary" onClick={fetchStatus}><RefreshCw size={14} /> Refresh</button>
        </div>
        {syncStatus ? (
          <div className="sync-status-content">
            <div className="sync-status-indicator">
              {syncStatus.connected ? (
                <><CheckCircle size={24} color="#10b981" /><span className="status-text connected">Connected</span></>
              ) : (
                <><AlertCircle size={24} color="#ef4444" /><span className="status-text disconnected">Disconnected</span></>
              )}
            </div>
            {syncStatus.connected && (
              <div className="sync-details">
                <div className="sync-detail-item"><span className="detail-label">Spreadsheet:</span><span className="detail-value">{syncStatus.spreadsheet_title}</span></div>
                <div className="sync-detail-item"><span className="detail-label">Sheets:</span><span className="detail-value">{syncStatus.worksheets?.join(', ')}</span></div>
                <a href={syncStatus.spreadsheet_url} target="_blank" rel="noopener noreferrer" className="sheet-link"><Link size={14} /> Open in Google Sheets</a>
              </div>
            )}
            {!syncStatus.connected && syncStatus.error && (
              <div className="sync-error-details" data-testid="sync-error-details">
                <p className="sync-error-text">{syncStatus.error}</p>
                {syncStatus.service_account_email && (
                  <div className="sync-share-info">
                    <p style={{fontSize:13,color:'#9ca3af',margin:'8px 0 4px'}}>Share your Google Sheet with this email (Editor access):</p>
                    <div className="sync-email-copy">
                      <code className="sync-email">{syncStatus.service_account_email}</code>
                      <button className="btn btn-sm btn-secondary" onClick={copyEmail} data-testid="copy-email-btn">
                        {copied ? <CheckCircle size={12} color="#10b981" /> : <Copy size={12} />}
                        {copied ? 'Copied!' : 'Copy'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : <div className="loading"><div className="spinner"></div></div>}
      </div>

      <div className="sync-actions-grid">
        <div className="sync-action-card featured">
          <div className="sync-action-icon sync"><RefreshCcw size={32} /></div>
          <h4>Sync from Sheets</h4>
          <p><strong>Read-only</strong> — imports new entries, updates statuses, skips duplicates.</p>
          <button className="btn btn-primary" onClick={() => handleSync('smart-sync')} disabled={syncing || !isSyncAvailable} data-testid="smart-sync-btn">
            {syncing ? <Loader2 size={16} className="animate-spin" /> : <RefreshCcw size={16} />} Sync Now
          </button>
        </div>
        <div className="sync-action-card">
          <div className="sync-action-icon pull"><CloudDownload size={32} /></div>
          <h4>Fresh Import</h4>
          <p>Import all data from Google Sheets. For first-time setup.</p>
          <button className="btn btn-secondary" onClick={() => handleSync('pull')} disabled={syncing || !isSyncAvailable} data-testid="pull-sync-btn">
            {syncing ? <Loader2 size={16} className="animate-spin" /> : <CloudDownload size={16} />} Pull All Data
          </button>
        </div>
      </div>
    </div>
  );
}
